import type { TargetAllocation } from '@interfaces/financialProduct';
import type { AssetClassTargetAllocation } from './assetClassTargetAllocation.types';

/**
 * The editor's row type carries display fields it recalculates on every render
 * (the actual weight, its badge colour, and whether the row was derived from a
 * security). Seed them so stored bands can round-trip through the editor.
 */
export const toEditableAllocations = (
  allocations: TargetAllocation[] = [],
): AssetClassTargetAllocation[] =>
  allocations.map((allocation) => ({
    ...allocation,
    actualPercentage: 0,
    color: 'supportOne',
    fromSecurity: false,
  }));

/** Drops the display fields so the draft stays shaped like the portfolio. */
export const toStoredAllocations = (
  allocations: AssetClassTargetAllocation[],
): TargetAllocation[] =>
  allocations.map(({ secondaryAssetClass, min, max }) => ({
    secondaryAssetClass,
    min,
    max,
  }));

/**
 * New rows are seeded with a random 10-digit id until an asset class is picked,
 * which is how the editor marks a row as unfinished. Real asset class ids are
 * ObjectIds, so they never collide with that length.
 */
const UNPICKED_ASSET_CLASS_ID_LENGTH = 10;

/** Bands that would be rejected downstream, so the step holds them here. */
export const hasUnusableAllocation = (
  allocations: AssetClassTargetAllocation[],
): boolean =>
  allocations.some(
    (allocation) =>
      allocation.secondaryAssetClass?.id?.toString().length ===
        UNPICKED_ASSET_CLASS_ID_LENGTH ||
      allocation.min > allocation.max ||
      allocation.min < 0 ||
      allocation.max > 100 ||
      (allocation.min === 0 && allocation.max === 0),
  );
